import { Header } from "@/header/header";
import { useMediaQuery } from "@/hooks/use-media-query";
import { cn } from "@/lib/utils";
import { Link, useLocation } from "react-router-dom";
import { 
  ChevronRight,
  Menu,
  X,
  BookOpen,
  TrendingUp,
  DollarSign,
  LayoutDashboard,
  Lock
} from "lucide-react";
import { useState } from "react";

type DocSection = {
  title: string;
  items: {
    label: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
  }[];
};

const DOC_SECTIONS: DocSection[] = [
  {
    title: "Getting Started",
    items: [
      { label: "Welcome to Nordex", href: "/docs/welcome", icon: BookOpen },
      { label: "Connecting Your Wallet", href: "/docs/connect-wallet", icon: BookOpen },
      { label: "Making Your First Deposit", href: "/docs/first-deposit", icon: BookOpen },
      { label: "Placing Your First Trade", href: "/docs/first-trade", icon: BookOpen },
      { label: "Understanding the Interface", href: "/docs/interface", icon: BookOpen },
    ],
  },
  {
    title: "Trading Guides",
    items: [
      { label: "Understanding Perpetual Futures", href: "/docs/perpetuals", icon: TrendingUp },
      { label: "Leverage Trading Explained", href: "/docs/leverage", icon: TrendingUp },
      { label: "Margin & Liquidation", href: "/docs/margin-liquidation", icon: TrendingUp },
      { label: "Funding Rates", href: "/docs/funding-rates", icon: TrendingUp },
      { label: "Order Types", href: "/docs/order-types", icon: TrendingUp },
      { label: "Position Management", href: "/docs/position-management", icon: TrendingUp },
    ],
  },
  {
    title: "Markets & Fees",
    items: [
      { label: "Available Markets", href: "/docs/markets", icon: DollarSign },
      { label: "Fee Structure", href: "/docs/fees", icon: DollarSign },
    ],
  },
  {
    title: "Platform Features",
    items: [
      { label: "Account Overview", href: "/docs/account", icon: LayoutDashboard },
      { label: "Charts & Analysis Tools", href: "/docs/charts", icon: LayoutDashboard },
    ],
  },
  {
    title: "Security & Best Practices",
    items: [
      { label: "Wallet Security", href: "/docs/wallet-security", icon: Lock },
      { label: "Understanding Risks", href: "/docs/understanding-risks", icon: Lock },
    ],
  },
];

type DocsLayoutProps = {
  children: React.ReactNode;
};

export const DocsLayout = ({ children }: DocsLayoutProps) => {
  const isMobile = useMediaQuery("(max-width: 1000px)");
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    <div className={cn("flex flex-col min-h-screen", isMobile ? "pb-14" : "pb-8")}>
      <Header />
      
      <div className="flex-1 flex relative bg-dot-pattern">
        {/* Sidebar */}
        <aside 
          className={cn(
            "w-72 border-r border-border/50 bg-background/50 backdrop-blur-sm overflow-y-auto shrink-0",
            isMobile && "fixed inset-y-0 left-0 z-50 transform transition-transform duration-300 shadow-2xl",
            isMobile && !sidebarOpen && "-translate-x-full"
          )}
        >
          {/* Mobile close button */}
          {isMobile && (
            <div className="flex items-center justify-between p-4 border-b border-border/50">
              <h2 className="font-semibold">Documentation</h2>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-2 hover:bg-accent rounded-lg transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* Align with header navigation - Logo + gap + nav items padding */}
          <nav className="py-12 pl-[4.5rem] pr-4 space-y-6">
            {DOC_SECTIONS.map((section) => (
              <div key={section.title}>
                <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 -ml-[4.5rem] pl-[4.5rem]">
                  {section.title}
                </h3>
                <ul className="space-y-1 -ml-[4.5rem]">
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = location.pathname === item.href;
                    
                    return (
                      <li key={item.href}>
                        <Link
                          to={item.href}
                          onClick={() => isMobile && setSidebarOpen(false)}
                          className={cn(
                            "flex items-center gap-3 pl-[4.5rem] pr-4 py-2 rounded-lg text-sm transition-colors",
                            isActive 
                              ? "bg-primary/10 text-primary font-medium" 
                              : "text-muted-foreground hover:text-foreground hover:bg-accent"
                          )}
                        >
                          <Icon className="h-4 w-4 flex-shrink-0" />
                          <span className="flex-1">{item.label}</span>
                          {isActive && <ChevronRight className="h-4 w-4" />}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* Mobile Sidebar Overlay */}
        {isMobile && sidebarOpen && (
          <div 
            className="fixed inset-0 bg-background/80 backdrop-blur-sm z-40"
            onClick={() => setSidebarOpen(false)}
          />
        )}

        {/* Main Content - aligned with sidebar */}
        <main className="flex-1 overflow-y-auto">
          {/* Mobile Menu Button */}
          {isMobile && (
            <button
              onClick={() => setSidebarOpen(true)}
              className="fixed bottom-20 right-4 z-30 p-3 rounded-full bg-primary text-primary-foreground shadow-lg hover:scale-105 transition-transform"
              aria-label="Open documentation menu"
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
          
          {/* Content wrapper with consistent top padding to align with sidebar */}
          <div className="py-12">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

